import { FC } from 'react';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Link from '@mui/material/Link';
import TableCell from 'components/TableCell';
import FlightSegments from 'components/FlightSegments';
import PackageEditTransportFlightsRowActions from 'components/PackageEditTransportFlightsRowActions';

import { PackageEditTransportFlightsRow_packageDate$key } from '__generated__/PackageEditTransportFlightsRow_packageDate.graphql';
import { PackageEditTransportFlightsRow_data$key } from '__generated__/PackageEditTransportFlightsRow_data.graphql';
import { PackageEditTransportFlightsRow_package$key } from '__generated__/PackageEditTransportFlightsRow_package.graphql';
import NonBreakingDate from 'components/NonBreakingDate';

type Props = {
  packageDate: PackageEditTransportFlightsRow_packageDate$key;
  data: PackageEditTransportFlightsRow_data$key;
  package: PackageEditTransportFlightsRow_package$key;
};

const PackageEditTransportFlightsRow: FC<Props> = props => {
  const packageDate = useFragment(
    graphql`
      fragment PackageEditTransportFlightsRow_packageDate on PackageDate {
        id
        start
        end

        flight {
          id
          forwardFlightNumber
          returnFlightNumber
          forwardChangeDuration
          returnChangeDuration
          priceLive

          forwardSegments {
            ...FlightSegments_segments
          }

          returnSegments {
            ...FlightSegments_segments
          }
        }

        ...PackageEditTransportFlightsRowActions_packageDate
      }
    `,
    props.packageDate
  );

  const data = useFragment(
    graphql`
      fragment PackageEditTransportFlightsRow_data on Query {
        ...PackageEditTransportFlightsRowActions_data
      }
    `,
    props.data
  );

  const pack = useFragment(
    graphql`
      fragment PackageEditTransportFlightsRow_package on Package {
        ...PackageEditTransportFlightsRowActions_package
      }
    `,
    props.package
  );

  const { id, start, end, flight } = packageDate;

  return (
    <TableRow>
      <TableCell center>
        <Link href={`/package-dates/${id}`}>
          <NonBreakingDate>{start}</NonBreakingDate> -{' '}
          <NonBreakingDate>{end}</NonBreakingDate>
        </Link>
      </TableCell>

      <TableCell center>
        {flight && <FlightSegments segments={flight.forwardSegments} />}
      </TableCell>

      <TableCell center>{flight?.forwardFlightNumber}</TableCell>

      <TableCell center>
        {flight && <FlightSegments segments={flight.returnSegments} />}
      </TableCell>

      <TableCell center>{flight?.returnFlightNumber}</TableCell>

      <TableCell center>
        {flight?.forwardChangeDuration && (
          <Box>
            <ArrowForwardIcon
              sx={{ display: 'inline-block', verticalAlign: 'middle' }}
            />
            {flight.forwardChangeDuration}
          </Box>
        )}

        {flight?.returnChangeDuration && (
          <Box>
            <ArrowBackIcon
              sx={{ display: 'inline-block', verticalAlign: 'middle' }}
            />
            {flight.returnChangeDuration}
          </Box>
        )}
      </TableCell>

      <TableCell center>{flight && `€${flight.priceLive}`}</TableCell>

      <TableCell center>
        <PackageEditTransportFlightsRowActions
          packageDate={packageDate}
          data={data}
          package={pack}
        />
      </TableCell>
    </TableRow>
  );
};

export default PackageEditTransportFlightsRow;
